import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useNavigate, useParams } from "react-router";

import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useScopeParams } from "@/hooks/useScopeParams";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import type { MasteredSite } from "../types";

type GeoSitesTabProps = {
  sites: MasteredSite[];
};

type SiteTableRow = {
  site_key: string;
  site_name: string;
  investigator_name: string | null;
  city: string | null;
  country: string;
  trial_count: number;
  total_enrolled: number;
};

function buildRows(sites: MasteredSite[]): SiteTableRow[] {
  return sites.map((site) => ({
    site_key: site.site_key,
    site_name: site.site_name,
    investigator_name: site.investigator_name,
    city: site.city,
    country: site.country,
    trial_count: site.trials.length,
    total_enrolled: site.trials.reduce((s, t) => s + (t.enrolled ?? 0), 0),
  }));
}

export default function GeoSitesTab({ sites }: GeoSitesTabProps) {
  const { buildUrl } = useScopeParams();
  const navigate = useNavigate();
  const { geoType } = useParams<{ geoType: string }>();
  const profileHidden = useFeatureFlag(FEATURE_FLAGS.SITE_PROFILE_LINKS_HIDDEN);

  const rows = buildRows(sites);
  const showCountry = geoType !== "country";

  const columns: ColumnsType<SiteTableRow> = [
    {
      title: "Site Name",
      dataIndex: "site_name",
      key: "site_name",
      render: (name: string, record) =>
        profileHidden ? (
          <span className="text-[13px] font-medium text-[#1F2933]">{name}</span>
        ) : (
          <button
            type="button"
            className="text-left text-[13px] font-medium text-[#1F2933] hover:text-[#0C98D0]"
            onClick={(e) => {
              e.stopPropagation();
              navigate(buildUrl(`site-profiles/${record.site_key}`));
            }}
          >
            {name}
          </button>
        ),
    },
    {
      title: "Principal Investigator",
      dataIndex: "investigator_name",
      key: "investigator_name",
      render: (name: string) => (
        <span className="text-[13px] text-[#4B5563]">{name}</span>
      ),
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      render: (val: string) => (
        <span className="text-[13px] text-[#6B7280]">{val}</span>
      ),
    },
    ...(showCountry
      ? [
          {
            title: "Country",
            dataIndex: "country",
            key: "country",
            render: (val: string) => (
              <span className="text-[13px] text-[#6B7280]">{val}</span>
            ),
          } as ColumnsType<SiteTableRow>[number],
        ]
      : []),
    {
      title: "# Trials",
      dataIndex: "trial_count",
      key: "trial_count",
      align: "center",
      render: (v: number) => (
        <span className="font-semibold text-[#1F2933]">{v}</span>
      ),
    },
  ];

  return (
    <div className="rounded-[8px] border border-[#F0F2F4] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.08)] overflow-hidden">
      <Table<SiteTableRow>
        dataSource={rows}
        columns={columns}
        rowKey="site_key"
        pagination={false}
        onRow={
          profileHidden
            ? undefined
            : (record) => ({
                onClick: () =>
                  navigate(buildUrl(`site-profiles/${record.site_key}`)),
                className: "cursor-pointer hover:bg-[#F8FAFC]",
              })
        }
      />
    </div>
  );
}
